import { Box } from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';
import React, { useRef } from 'react';
import Address from '../models/Address';
import AddressMapResult, { CustomMapBounds } from '../models/AddressMapResult';
import { Palette } from 'Palette';
import MultiMapControls from './multiMap/MultiMapControls';
import MultiMapMarkers from './multiMap/MultiMapMarkers';
import MultiMapPrintView from './multiMap/MultiMapPrintView';
import { isContainerMeasurable } from './multiMap/multiMapUtils';
import { useMultiMapContainerReady } from './multiMap/useMultiMapContainerReady';
import { useMultiMapFullscreen } from './multiMap/useMultiMapFullscreen';
import { useMultiMapLoad } from './multiMap/useMultiMapLoad';

interface MultiMapProps {
  addresses: Address[] | AddressMapResult[];
  height?: string;
  width?: string;
  zoomOnSingle?: number;
  mapBounds?: CustomMapBounds;
  printMode?: boolean;
}

const MultiMap: React.FC<MultiMapProps> = ({
  addresses,
  height = '100%',
  width = '100%',
  zoomOnSingle = 15,
  mapBounds,
  printMode = false,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { fullscreen, toggleFullscreen, closeFullscreen } = useMultiMapFullscreen();
  const containerReady = useMultiMapContainerReady(containerRef, fullscreen, addresses.length);
  const { mapState, onLoad, resetMap } = useMultiMapLoad(
    addresses,
    zoomOnSingle,
    containerRef,
    mapRef,
    fullscreen,
  );

  const shouldRenderMap = containerReady && isContainerMeasurable(containerRef.current);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          position: fullscreen ? 'fixed' : 'relative',
          top: fullscreen ? '10vh' : 'auto',
          left: fullscreen ? '5vw' : 'auto',
          width: fullscreen ? '90vw' : width,
          height: fullscreen ? '80vh' : height,
          zIndex: fullscreen ? 1300 : 'auto',
          borderRadius: '1rem',
          borderWidth: fullscreen ? 0 : 1,
          borderStyle: fullscreen ? 'none' : 'solid',
          borderColor: Palette['tone1'].border,
          overflow: 'hidden',
          background: '#fff',
          boxShadow: fullscreen ? 4 : 0,
          transition: 'all 0.3s ease',
        }}
      >
        {printMode ? (
          <MultiMapPrintView
            addresses={addresses}
            mapBounds={mapBounds}
            zoomOnSingle={zoomOnSingle}
          />
        ) : (
          shouldRenderMap && (
            <GoogleMap
              options={{
                fullscreenControl: false,
                mapTypeId: mapState.mapTypeId,
                gestureHandling: 'greedy',
              }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
              zoom={mapState.zoom}
              center={mapState.center}
              onLoad={onLoad}
            >
              <MultiMapMarkers addresses={addresses} />
            </GoogleMap>
          )
        )}
        <MultiMapControls
          fullscreen={fullscreen}
          onToggleFullscreen={toggleFullscreen}
          onResetMap={resetMap}
        />
      </Box>
      {fullscreen && (
        <Box
          onClick={closeFullscreen}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 1299,
          }}
        />
      )}
    </>
  );
};

export default MultiMap;
